/*
 * Copyright (c) 2026. Orion Analytics
 */

import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { fetchMacros } from "../../api";

const darkLayout = (title, yTitle = "") => ({
    height: 420,

    title: {
        text: title,
        font: {
            color: "#f8fafc",
            size: 18
        }
    },

    paper_bgcolor: "#020617",
    plot_bgcolor: "#020617",

    font: {
        color: "#cbd5e1"
    },

    margin: {
        l: 60,
        r: 20,
        t: 50,
        b: 50
    },

    xaxis: {
        showgrid: false,
        zeroline: false
    },

    yaxis: {
        title: yTitle,
        gridcolor: "#1e293b"
    },

    legend: {
        orientation: "h"
    }
});

function ChartCard({ children }) {
    return (
        <div
            className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-4
            "
        >
            {children}
        </div>
    );
}

export default function MacroFI({ activeWidget }) {

    const [data, setData] = useState(null);

    useEffect(() => {

        fetchMacros()
            .then(res => {
                setData(res);
            })
            .catch(err => {
                console.error(err);
            });

    }, []);

    if (!data) {
        return (
            <div className="text-slate-400 p-6">
                Loading macro data...
            </div>
        );
    }

    /*
    ======================================================
    RATES & YIELD CURVE
    ======================================================
    */

    if (activeWidget === "Rates & Yield Curve") {

        return (

            <div className="space-y-6">

                <ChartCard>

                    <Plot
                        data={[
                            {
                                x: data.inr_zc_curve.map(d => d.tenor),
                                y: data.inr_zc_curve.map(d => d.yield),
                                type: "scatter",
                                mode: "lines+markers",
                                line: {
                                    width: 3
                                }
                            }
                        ]}
                        layout={darkLayout(
                            `INR Yield Curve (${data.inr_zc_curve?.[0]?.curvedate || ""})`,
                            "Yield (%)"
                        )}
                        config={{
                            displayModeBar: false,
                            responsive: true
                        }}
                        style={{ width: "100%" }}
                    />

                </ChartCard>

                <div
                    className="
                        grid
                        grid-cols-1
                        xl:grid-cols-2
                        gap-6
                    "
                >

                    <ChartCard>
                        <Plot
                            data={[
                                {
                                    x: data.inr_rate_90D.map(d => d.period),
                                    y: data.inr_rate_90D.map(d => d.value),
                                    type: "scatter",
                                    mode: "lines"
                                }
                            ]}
                            layout={darkLayout(
                                "90 Day Treasury Bill",
                                "%"
                            )}
                            config={{ responsive: true }}
                            style={{ width: "100%" }}
                        />
                    </ChartCard>

                    <ChartCard>
                        <Plot
                            data={[
                                {
                                    x: data.inr_rate_180D.map(d => d.period),
                                    y: data.inr_rate_180D.map(d => d.value),
                                    type: "scatter",
                                    mode: "lines"
                                }
                            ]}
                            layout={darkLayout(
                                "180 Day Treasury Bill",
                                "%"
                            )}
                            config={{ responsive: true }}
                            style={{ width: "100%" }}
                        />
                    </ChartCard>

                    <ChartCard>
                        <Plot
                            data={[
                                {
                                    x: data.inr_rate_1Y.map(d => d.period),
                                    y: data.inr_rate_1Y.map(d => d.value),
                                    type: "scatter",
                                    mode: "lines"
                                }
                            ]}
                            layout={darkLayout(
                                "1 Year Treasury",
                                "%"
                            )}
                            config={{ responsive: true }}
                            style={{ width: "100%" }}
                        />
                    </ChartCard>

                    <ChartCard>
                        <Plot
                            data={[
                                {
                                    x: data.inr_rate_10Y.map(d => d.period),
                                    y: data.inr_rate_10Y.map(d => d.value),
                                    type: "scatter",
                                    mode: "lines"
                                }
                            ]}
                            layout={darkLayout(
                                "10 Year G-Sec",
                                "%"
                            )}
                            config={{ responsive: true }}
                            style={{ width: "100%" }}
                        />
                    </ChartCard>

                </div>

            </div>
        );
    }

    /*
    ======================================================
    MACRO INDICATORS
    ======================================================
    */

    if (activeWidget === "Macro Indicators") {

        return (

            <div
                className="
                    grid
                    grid-cols-1
                    xl:grid-cols-2
                    gap-6
                "
            >

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.gdp.map(d => d.period),
                            y: data.gdp.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("GDP")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.fx_reserves.map(d => d.period),
                            y: data.fx_reserves.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("FX Reserves")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.cpi.map(d => d.period),
                            y: data.cpi.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("Consumer Price Index")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.wpi.map(d => d.period),
                            y: data.wpi.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("Wholesale Price Index")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.iip.map(d => d.period),
                            y: data.iip.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("Industrial Production")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

            </div>
        );
    }

    /*
    ======================================================
    GOVT FISCALS
    ======================================================
    */

    if (activeWidget === "Govt Fiscals") {

        return (

            <div
                className="
                    grid
                    grid-cols-1
                    xl:grid-cols-2
                    gap-6
                "
            >

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.ex_debt.map(d => d.period),
                            y: data.ex_debt.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("External Debt")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.fx_reserves.map(d => d.period),
                            y: data.fx_reserves.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("FX Reserves")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.bop.map(d => d.period),
                            y: data.bop.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("Balance of Payments")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

                <ChartCard>
                    <Plot
                        data={[{
                            x: data.sg_debt.map(d => d.period),
                            y: data.sg_debt.map(d => d.value),
                            type: "scatter",
                            mode: "lines"
                        }]}
                        layout={darkLayout("State Government Debt")}
                        style={{ width: "100%" }}
                    />
                </ChartCard>

            </div>
        );
    }

    /*
    ======================================================
    BANKING & CREDIT
    ======================================================
    */

    if (activeWidget === "Banking & Credit") {

        return (

            <div
                className="
                    grid
                    grid-cols-1
                    xl:grid-cols-2
                    gap-6
                "
            >

                <ChartCard>

                    <Plot
                        data={[
                            {
                                x: data.repo.map(d => d.period),
                                y: data.repo.map(d => d.value),
                                name: "Repo",
                                type: "scatter",
                                mode: "lines"
                            },
                            {
                                x: data.revrepo.map(d => d.period),
                                y: data.revrepo.map(d => d.value),
                                name: "Reverse Repo",
                                type: "scatter",
                                mode: "lines"
                            }
                        ]}
                        layout={darkLayout("RBI Policy Rates")}
                        style={{ width: "100%" }}
                    />

                </ChartCard>

                <ChartCard>

                    <Plot
                        data={[
                            {
                                x: data.crr.map(d => d.period),
                                y: data.crr.map(d => d.value),
                                name: "CRR",
                                type: "scatter",
                                mode: "lines"
                            },
                            {
                                x: data.slr.map(d => d.period),
                                y: data.slr.map(d => d.value),
                                name: "SLR",
                                type: "scatter",
                                mode: "lines"
                            }
                        ]}
                        layout={darkLayout("Liquidity Metrics")}
                        style={{ width: "100%" }}
                    />

                </ChartCard>

                <ChartCard>

                    <Plot
                        data={[
                            {
                                x: data.bankcredit.map(d => d.period),
                                y: data.bankcredit.map(d => d.value),
                                name: "Credit",
                                type: "scatter",
                                mode: "lines"
                            },
                            {
                                x: data.bankdeposit.map(d => d.period),
                                y: data.bankdeposit.map(d => d.value),
                                name: "Deposit",
                                type: "scatter",
                                mode: "lines"
                            }
                        ]}
                        layout={darkLayout("Bank Credit vs Deposits")}
                        style={{ width: "100%" }}
                    />

                </ChartCard>

                <ChartCard>

                    <Plot
                        data={[
                            {
                                x: data.cdratio.map(d => d.period),
                                y: data.cdratio.map(d => d.value),
                                name: "CD Ratio",
                                type: "scatter",
                                mode: "lines"
                            },
                            {
                                x: data.crratio.map(d => d.period),
                                y: data.crratio.map(d => d.value),
                                name: "CR Ratio",
                                type: "scatter",
                                mode: "lines"
                            }
                        ]}
                        layout={darkLayout("Credit / Cash Ratios")}
                        style={{ width: "100%" }}
                    />

                </ChartCard>

            </div>
        );
    }

    /*
    ======================================================
    FUTURE
    ======================================================
    */

    return (

        <div
            className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-8
                text-slate-400
            "
        >
            Corporate Bond Analytics Coming Soon
        </div>

    );
}